import { useState, useEffect } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { cn } from '@/lib/utils';
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

// Brand colors from design system
const MAGENTA_BEAM = "#FF4DB8";
const SPARK_BLUE = "#4E9CFF";

export type GenieExample = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  logoUrl: string | null;
  phoneImageUrl: string;
  brandColor: string;
  phoneNumbers?: {
    country: string;
    code: string;
    number: string;
    flag: string;
  }[];
  phoneNumber: string; // fallback
  webCallUrl?: string;
  qrCodeUrl?: string;
};

type GenieExampleModalProps = {
  genie: GenieExample | null;
  isOpen: boolean;
  onClose: () => void;
};

type OptionKey = 'live-link' | 'phone' | 'qr-code' | 'web-embed';

// Widget configuration for web embed
const getWidgetConfig = (genie: GenieExample) => ({
  agentUrl: genie.id,
  position: 'bottom-right',
  primaryColor: genie.brandColor || '#8267fc',
  buttonColor: genie.brandColor || '#8267fc',
  baseUrl: 'https://helpgenie.ai',
  agentName: genie.name,
  buttonText: "Let's chat!",
  logoUrl: genie.logoUrl || undefined,
});

declare global {
  interface Window {
    hg: any;
  }
}

interface OptionCardProps {
  genieIcon?: GenieIconName;
  icon?: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  isActive: boolean;
}

const OptionCard = ({ genieIcon, icon, title, description, onClick, isActive }: OptionCardProps) => (
  <button
    onClick={onClick}
    className={`
      flex flex-col items-center p-5 sm:p-6 rounded-2xl transition-all duration-300 hover:scale-[1.03]
      min-w-[140px] sm:min-w-[160px] max-w-[200px] flex-1 bg-white
      ${isActive ? 'shadow-xl' : 'shadow-md hover:shadow-lg'}
    `}
    style={{
      border: isActive ? `2px solid ${MAGENTA_BEAM}` : '2px solid transparent',
      background: isActive
        ? `linear-gradient(white, white) padding-box, linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE}) border-box`
        : 'white',
    }}
  >
    <div
      className="mb-3 p-3 sm:p-4 rounded-xl"
      style={{
        background: `linear-gradient(135deg, ${MAGENTA_BEAM}15, ${SPARK_BLUE}15)`,
      }}
    >
      {genieIcon ? (
        <GenieIcon
          name={genieIcon}
          size={36}
          color={MAGENTA_BEAM}
          secondaryColor={SPARK_BLUE}
          gradientEnabled={true}
          gradientAngle={135}
          label={title}
        />
      ) : (
        <div style={{ color: MAGENTA_BEAM }}>{icon}</div>
      )}
    </div>
    <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">{title}</h3>
    <p className="text-xs sm:text-sm text-gray-500 text-center">{description}</p>
  </button>
);

export const GenieExampleModal = ({ genie, isOpen, onClose }: GenieExampleModalProps) => {
  const [activeOption, setActiveOption] = useState<OptionKey | null>(null);
  const [copiedPhone, setCopiedPhone] = useState<string | null>(null);
  const [widgetLoaded, setWidgetLoaded] = useState(false);

  // Only use phone numbers if they actually have numbers
  const phoneNumbers = genie?.phoneNumbers?.filter(pn => pn.number) ||
    (genie?.phoneNumber ? [{ country: 'United States', code: 'US', number: genie.phoneNumber, flag: '🇺🇸' }] : []);

  const hasPhoneNumbers = phoneNumbers.length > 0;

  const liveAgentUrl = genie ? `${window.location.origin}/live-agent/${genie.id}` : '';

  // Load widget script when web-embed is selected
  useEffect(() => {
    if (activeOption === 'web-embed' && !widgetLoaded && genie) {
      // Initialize hg function
      window.hg = window.hg || function(...args: unknown[]) {
        (window.hg.q = window.hg.q || []).push(args);
      };
      window.hg.q = window.hg.q || [];

      // Load widget script
      const script = document.createElement('script');
      script.id = 'hg-modal';
      script.src = 'https://helpgenie.ai/widget.js';
      script.async = true;
      script.onload = () => {
        window.hg('init', getWidgetConfig(genie));
        setWidgetLoaded(true);
      };

      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      }
    }
  }, [activeOption, widgetLoaded, genie]);

  // Cleanup widget when closing modal
  useEffect(() => {
    if (!isOpen) {
      const widgetElements = document.querySelectorAll('[id^="hg-"], [class^="hg-"]');
      widgetElements.forEach(el => el.remove());
      const script = document.getElementById('hg-modal');
      if (script) script.remove();
      setWidgetLoaded(false);
      setActiveOption(null);
    }
  }, [isOpen]);

  if (!isOpen || !genie) return null;

  const handleCopyPhone = async (number: string) => {
    try {
      await navigator.clipboard.writeText(number);
      setCopiedPhone(number);
      setTimeout(() => setCopiedPhone(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOptionClick = (option: OptionKey) => {
    if (option === 'live-link') {
      window.location.href = `/live-agent/${genie.id}`;
      return;
    }
    setActiveOption(activeOption === option ? null : option);
  };

  const options = [
    {
      key: 'live-link' as OptionKey,
      genieIcon: 'chat' as const,
      title: 'Live Link',
      description: 'Open the live conversation page',
    },
    {
      key: 'phone' as OptionKey,
      genieIcon: 'phone' as const,
      title: 'Phone',
      description: 'Call and talk to the Genie',
    },
    {
      key: 'qr-code' as OptionKey,
      genieIcon: 'qr-code' as const,
      title: 'QR Code',
      description: 'Scan with your phone',
    },
    {
      key: 'web-embed' as OptionKey,
      genieIcon: 'code' as const,
      title: 'Web Embed',
      description: 'See the widget in action',
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/20 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdropClick}
    >
      {/* Side Panel - responsive width: full on mobile, 70% on tablet, 60% on desktop, max 5xl */}
      <div className="relative w-full sm:w-[85%] md:w-[75%] lg:w-[60%] max-w-5xl h-full bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 p-3 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-all"
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        {/* Header with gradient background */}
        <div
          className="px-6 sm:px-10 lg:px-14 pt-10 pb-8"
          style={{ background: `linear-gradient(135deg, ${MAGENTA_BEAM}08, white 50%, ${SPARK_BLUE}08)` }}
        >
          <div className="flex items-center gap-5 mb-5">
            {/* Logo */}
            <div className="w-18 h-18 sm:w-20 sm:h-20 rounded-2xl bg-white border border-gray-100 overflow-hidden flex-shrink-0 shadow-sm flex items-center justify-center">
              {genie.logoUrl ? (
                <img
                  src={genie.logoUrl}
                  alt={`${genie.name} logo`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-2xl font-bold text-white"
                  style={{
                    background: `linear-gradient(135deg, ${genie.brandColor || MAGENTA_BEAM}, ${SPARK_BLUE})`,
                  }}
                >
                  {genie.name.charAt(0)}
                </div>
              )}
            </div>

            {/* Brand info */}
            <div>
              <h2
                className="text-2xl sm:text-3xl font-bold mb-1"
                style={{
                  background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {genie.name}
              </h2>
              <p className="text-gray-500 text-lg">{genie.tagline}</p>
            </div>
          </div>

          {/* Description */}
          <p className="text-gray-600 leading-relaxed text-base sm:text-lg max-w-2xl">
            {genie.description}
          </p>
        </div>

        {/* Main content area */}
        <div className="px-6 sm:px-10 lg:px-14 py-8">
          {/* Section title */}
          <p className="text-gray-500 text-center mb-4">Choose how you'd like to connect</p>

          {/* Options Grid */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-5 mb-8">
            {options.map((option) => (
              <OptionCard
                key={option.key}
                genieIcon={option.genieIcon}
                title={option.title}
                description={option.description}
                onClick={() => handleOptionClick(option.key)}
                isActive={activeOption === option.key}
              />
            ))}
          </div>

          {/* Expanded Content - Phone Numbers */}
          {activeOption === 'phone' && (
            <div
              className="p-5 rounded-2xl animate-in fade-in duration-300 bg-white/80 backdrop-blur-sm"
              style={{ border: `2px solid ${MAGENTA_BEAM}40` }}
            >
              {hasPhoneNumbers ? (
                <>
                  <p className="text-gray-600 text-center mb-4">Call this number to talk to the Genie:</p>
                  <div className="space-y-3">
                    {phoneNumbers.map((phone) => (
                      <div key={phone.code} className="flex items-center gap-3">
                        <span className="text-2xl">{phone.flag}</span>
                        <a
                          href={`tel:${phone.number.replace(/\D/g, '')}`}
                          className="flex-1 text-lg font-semibold py-3 px-4 rounded-xl text-center transition-all hover:shadow-lg hover:scale-[1.01] bg-white border border-gray-200 hover:border-gray-300 text-indigo-600"
                        >
                          {phone.number}
                        </a>
                        <button
                          onClick={() => handleCopyPhone(phone.number)}
                          className={cn(
                            'p-3 rounded-xl border transition-all',
                            copiedPhone === phone.number
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          )}
                          title="Copy phone number"
                        >
                          {copiedPhone === phone.number ? (
                            <Check size={20} className="text-green-500" />
                          ) : (
                            <Copy size={20} className="text-gray-500" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-center">
                  No phone number is currently configured for this Genie. Try using the Live Link or Web Embed options instead.
                </p>
              )}
            </div>
          )}

          {/* Expanded Content - QR Code */}
          {activeOption === 'qr-code' && (
            <div
              className="p-5 rounded-2xl text-center animate-in fade-in duration-300 bg-white/80 backdrop-blur-sm"
              style={{ border: `2px solid ${MAGENTA_BEAM}40` }}
            >
              <p className="text-gray-600 mb-4">Scan this QR code with your phone:</p>
              <div
                className="inline-block p-4 bg-white rounded-xl shadow-lg"
                style={{ border: `2px solid ${MAGENTA_BEAM}30` }}
              >
                <QRCodeSVG
                  value={liveAgentUrl}
                  size={160}
                  level="M"
                  includeMargin={false}
                  fgColor={MAGENTA_BEAM}
                />
              </div>
              <p className="text-sm text-gray-400 mt-4">
                Opens the live conversation on your mobile device
              </p>
            </div>
          )}

          {/* Expanded Content - Web Embed */}
          {activeOption === 'web-embed' && (
            <div
              className="p-5 rounded-2xl text-center animate-in fade-in duration-300 bg-white/80 backdrop-blur-sm"
              style={{ border: `2px solid ${MAGENTA_BEAM}40` }}
            >
              <p className="text-gray-600 mb-2">
                {widgetLoaded
                  ? "The widget is now active! Look for it in the bottom-right corner."
                  : "Loading widget..."
                }
              </p>
              <p className="text-sm text-gray-400">
                This is how the Genie appears when embedded on your website
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-10 lg:px-14 py-6 mt-auto border-t border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-500 text-center">
            This is a demo Genie powered by Help Genie. Your conversation helps demonstrate After Hours capabilities.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.25s ease-out forwards;
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
};
